/**
 * The aged care BASELINE — "enters care, pays the full DAP, makes no strategic decisions" — computed
 * once, in one place, for every surface that compares against it.
 *
 * WHY THIS EXISTS (owner direction, 2026-08-26). Aged care is not a lifestyle choice. The client is
 * entering residential care whatever we advise, so the honest question is never "how does this
 * compare with life at home" — it is "what did our advice add on top of the care that was happening
 * anyway". Measuring strategies against today's at-home position credits every recommendation with
 * the care event itself: on a real couple that is a $15,376 Age Pension rise and an $82,273 cashflow
 * fall which no strategy caused and none can avoid.
 *
 * TWO BASES, DELIBERATELY. The Financial Impact panel is not one thing:
 *   - Age Pension and Cash Flow are a NORMALISED year — "what would 12 months look like if the plan
 *     were in place today", timing stripped out on purpose. Their baseline must be struck the same
 *     way, with `calculateFinancials`, or the delta stops being strategy-only.
 *   - The estate is the last row of a real PROJECTION, timing-aware and respecting the care start
 *     date. Its baseline must be a projection too, run over the same years with the same care date.
 * Using the projection's year one for the annual tiles would look obvious and be wrong: it blends
 * part-year timing into figures whose whole purpose is to have none.
 *
 * THE TRAP THIS MODULE EXISTS TO CLOSE. Two facts about aged care travel in places that make them
 * easy to drop when building a "no strategy" position, and dropping either is silent:
 *
 *   1. WHO IS IN CARE is passed as *strategy options* (`clientResidencyOverride` /
 *      `partnerResidencyOverride`, from `agedCareEntrantResidencyOverrides`), in the same object
 *      as the strategies themselves. Strip the strategies naively and the residency goes with them —
 *      the couple is then assessed as two people living at home, the illness-separated treatment
 *      never fires, and the baseline pension is understated by thousands. Every strategy then
 *      inherits that gap as a phantom improvement.
 *   2. THE FACT-FIND ITSELF IS MUTATED by the strategy (`simulatedFactFind` has the home already
 *      sold, the RAD already paid, downsizer contributions already made). A baseline built on it is
 *      not a baseline — it keeps the strategy's balance sheet and drops only its options.
 *
 * The pre-existing SOA "Do Nothing" fell into BOTH: it ran `simulatedFactFind` with no residency
 * override while charging full care fees, i.e. it assessed the couple as living at home and billed
 * them for a nursing home at the same time. This module takes the ORIGINAL fact-find and carries the
 * residency override explicitly, so neither can happen again.
 */
import { withDailyPaymentOverride } from './householdCareCosts.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { agedCareEntrantResidencyOverrides } from '../../../../shared/src/calculation/householdStatus.ts';
import { DEFAULT_INFLATION_ASSUMPTION_PCT } from '../../constants/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { FinancialPosition } from './FinancialImpactPanel.tsx';

export interface AgedCareBaselineInput {
    /** The ORIGINAL fact-find. Never the simulated one — see the header. */
    factFind: DraftFactFind;
    radInput: number;
    legacyYears: number;
    /** The adjusted living expenses the strategy runs on, so the delta is not polluted by them. */
    scenarioLivingExpenses: number | string | undefined;
    /** 'financial_year' | 'calendar_year' — must match the strategy run it will be compared with. */
    projectionPeriodMode?: string;
    /** The nominated care start date, so baseline and strategy sit on the same timeline. */
    careStartDate?: string;
    /** Also run the no-plan projection over this many years, for the step-2 runway. */
    runwayYears?: number;
    /** The second room, where both of a couple enter care: priced with no deposit, like the first. */
    secondRoom?: { person: 'client' | 'partner'; roomPrice: number; careStartDate?: string; extraServiceAnnual?: number; entersLater?: boolean; livingExpensesOnceBoth?: number } | null;
    /**
     * Someone in care NOW (8 Oct 2026): the baseline is STAYING PUT — their current room, the deposit
     * they hold, the daily payment they actually pay — not "nothing paid up front". The projection
     * opens with that deposit on the balance sheet (the engine adds it from the profile).
     */
    existingStay?: { person: 'client' | 'partner'; roomPrice: number; refundToday: number; dailyPayment: number } | null;
    /**
     * Leaving a retirement village (8 Oct 2026): with no plan the exit entitlement is paid on the
     * refund date and banked — nothing of it goes to the room. The interest counts until then.
     */
    villageExit?: { person: 'client' | 'partner'; leaveDate: string; refundDate: string; entitlement: number; paidAtMove?: boolean; feesAfterLeaving?: { perDay: number; fullDays: number; sharedDays: number; sharePct: number; total: number } } | null;
}

/** The no-plan position from the second person's entry: both in care, both rooms paid for. */
export interface AgedCareBaselinePhaseTwo {
    fromDate: string;
    financials: FinancialPosition;
    /** The first entrant's schedule once both are in care. */
    agedCareCosts: Record<string, unknown>;
    secondAgedCareCosts: Record<string, unknown> | null;
    cashFlowAfterCare: number;
    pension: number;
}

export interface AgedCareBaseline {
    /** Normalised, timing-free. The basis for the Age Pension, Cash Flow and Net Worth tiles. */
    financials: FinancialPosition | null;
    /**
     * The baseline's cashflow AFTER its own care costs — the only cashflow figure any surface should
     * quote as "with no plan".
     *
     * `financials.cashFlow` is the household's cashflow BEFORE care fees, because `calculateFinancials`
     * knows nothing about them; they are struck separately and subtracted by the caller. That was
     * harmless while the baseline was "today, at home", which incurs no care fees — and became a
     * $97,649 error the moment the baseline became "in care, no plan", which incurs the full DAP.
     * The panel showed $24,414 where the plan summary showed −$73,235 for the same baseline, on the
     * same screen (owner-caught, 2026-08-26). Computed once, here, so no caller has to remember.
     */
    cashFlowAfterCare: number | null;
    /** The baseline Age Pension, so no surface re-reads it from a different object. */
    pension: number | null;
    /**
     * The household as it is TODAY, still at home — needed only for the walk-in note's "today → once
     * in care" bridge. Not a benchmark for anything: see the header on why that comparison flatters.
     */
    todayFinancials: FinancialPosition | null;
    /** The Do Nothing projection — the basis for the estate. */
    projections: Record<string, unknown>[];
    /** Projected estate at the horizon: the last row's net worth. Null when it could not be run. */
    legacyValue: number | null;
    /**
     * The same no-plan projection over the RUNWAY horizon (25 years), for "how long the money lasts"
     * on step 2. `projections` above runs only to the legacy horizon (5 years by default), and a
     * runway struck on five rows says "beyond 25 years" about a position it never tested — which is
     * exactly what it did on the first build of the walk-in hero (22 Sep 2026). Empty when the caller
     * did not ask for it.
     */
    runwayProjections: Record<string, unknown>[];
    /** Care costs with no RAD paid — full DAP, which is what "no decisions" actually costs. */
    agedCareCosts: Record<string, unknown> | null;
    /** The second room's no-plan schedule, where both enter care; null otherwise. */
    secondAgedCareCosts?: Record<string, unknown> | null;
    /** True where the second person enters later than the first: the figures above are the first stage. */
    secondEntersLater?: boolean;
    /** The second stage, where the second person enters later; null otherwise. */
    phase2?: AgedCareBaselinePhaseTwo | null;
}

export const EMPTY_AGED_CARE_BASELINE: AgedCareBaseline = {
    financials: null,
    cashFlowAfterCare: null,
    pension: null,
    todayFinancials: null,
    projections: [],
    runwayProjections: [],
    legacyValue: null,
    agedCareCosts: null,
};

/**
 * The strategy options that describe DOING NOTHING: no property decision, no RAD, no equity release,
 * no gifting/bonds/annuity. Everything a client could choose is switched off; everything that is
 * true regardless of choice (who is in care, what the household spends) is carried.
 */
export function buildDoNothingStrategyOptions(
    factFind: DraftFactFind,
    scenarioLivingExpenses: number | string | undefined,
    extras: Record<string, unknown> = {},
): Record<string, unknown> {
    return {
        // NOT a strategy — a fact about the household, and the reason the couple is assessed as
        // illness-separated. Carried explicitly so it can never be stripped with the strategies.
        ...agedCareEntrantResidencyOverrides(factFind as unknown as FactFind),
        scenarioLivingExpenses,
        rentHome: false,
        downsizeHome: false,
        accessHomeEquity: false,
        reverseMortgage: false,
        heas: false,
        downsizeDetails: {},
        radFunding: {},
        reverseMortgageDetails: null,
        heasDetails: null,
        otherStrategies: { gifting: false, funeralBonds: false, annuity: false },
        annuityRecord: null,
        inflationAssumptionPct: DEFAULT_INFLATION_ASSUMPTION_PCT,
        investmentPropertySaleSummary: null,
        ...extras,
    };
}

/**
 * Build the baseline on both bases. Returns {@link EMPTY_AGED_CARE_BASELINE} rather than throwing:
 * a surface that cannot get a baseline must fall back to saying nothing, never to a wrong number.
 */
export async function buildAgedCareBaseline(input: AgedCareBaselineInput): Promise<AgedCareBaseline> {
    const { factFind, legacyYears, scenarioLivingExpenses, projectionPeriodMode, runwayYears, secondRoom, existingStay, villageExit } = input;
    // Staying put: the current room and deposit are the baseline, and care is under way (no entry date).
    const radInput = existingStay ? existingStay.roomPrice : input.radInput;
    const careStartDate = existingStay ? undefined : input.careStartDate;
    if (!factFind) return EMPTY_AGED_CARE_BASELINE;

    try {
        const doNothingOptions = buildDoNothingStrategyOptions(factFind, scenarioLivingExpenses, {
            ...(projectionPeriodMode ? { projectionPeriodMode } : {}),
            // A village unit paid out at the move: the interest is sold from entry (8 Oct 2026).
            ...(villageExit?.paidAtMove && villageExit.entitlement > 0 ? { villageInterestSold: true } : {}),
            // Same timeline as the strategy: a baseline that enters care today cannot be compared
            // with a strategy that enters in two years — every delta would carry the difference.
            ...(careStartDate ? { careStartDate } : {}),
        });
        const secondRoomActive = !!secondRoom && secondRoom.roomPrice > 0;

        // The household as it is today, at home. Used ONLY for the walk-in note's bridge sentence.
        const todayFinancials = (await calculationApi.calculateFinancials(factFind)) as FinancialPosition;
        // The household IN CARE with no plan, on the adjusted living costs: the NORMALISED basis the
        // annual tiles' "after" is struck on. The fee schedules below are struck on it too — the
        // means-tested fees read the in-care income (the illness-separated pension) and assets, and
        // the home's exemption reads who is left living in it. Striking them on today's at-home
        // position priced the rail a few dollars a day apart from step 2 (owner-caught, 7 Oct 2026).
        // Where the second person enters LATER, the normalised year is the FIRST stage — the other
        // still at home (the home exempt, the pension illness-separated) — and the second stage is
        // struck beside it (carePhases.ts). The projection runs the whole timeline either way.
        const later = secondRoomActive && !!secondRoom!.entersLater;
        const residencyBoth = agedCareEntrantResidencyOverrides(factFind as unknown as FactFind);
        const secondKey = secondRoom?.person === 'client' ? 'clientResidencyOverride' : 'partnerResidencyOverride';
        const secondRecorded = (secondRoom?.person === 'client' ? factFind.client?.currentResidency : factFind.partner?.currentResidency) || 'own_home';
        const residencyInCare = later ? { ...residencyBoth, [secondKey]: secondRecorded } : residencyBoth;
        const financials = (await calculationApi.calculateFinancials(factFind, { ...doNothingOptions, ...residencyInCare })) as FinancialPosition;
        // The second stage's living costs, where the plan gives a figure for once both are in care.
        const livingOnceBoth = later && typeof secondRoom!.livingExpensesOnceBoth === 'number' && secondRoom!.livingExpensesOnceBoth > 0 ? secondRoom!.livingExpensesOnceBoth : null;
        const financialsBoth = later
            ? ((await calculationApi.calculateFinancials(factFind, { ...doNothingOptions, ...(livingOnceBoth != null ? { scenarioLivingExpenses: livingOnceBoth } : {}) })) as FinancialPosition)
            : financials;

        // Struck on the ORIGINAL fact-find, with care costs the client has not acted on: no RAD, so
        // the whole room price is charged as a daily payment. That is what making no decision costs.
        const agedCareCostsStruck = (await calculationApi.calculateAgedCareCosts({
            factFind,
            financials,
            radInput,
            extraServiceFeeInput: 0,
            radPaid: existingStay ? existingStay.refundToday : 0,
            overallStrategy: { rentHome: false, downsizeHome: false, accessHomeEquity: false, reverseMortgage: false, heas: false, ...residencyInCare },
            // Whose schedule this is, where two people are in care (their own DAP in their own means).
            ...(secondRoomActive ? { resident: secondRoom!.person === 'client' ? 'partner' : 'client' } : {}),
            ...(existingStay ? { resident: existingStay.person } : {}),
        })) as Record<string, unknown>;
        // The home someone is in now charges the daily payment their agreement fixed, not today's.
        const agedCareCosts = existingStay ? (withDailyPaymentOverride(agedCareCostsStruck as never, existingStay.dailyPayment) as Record<string, unknown>) : agedCareCostsStruck;
        // The second room, likewise undecided: the whole price paid daily (7 Oct 2026).
        const secondAgedCareCosts = secondRoomActive
            ? ((await calculationApi.calculateAgedCareCosts({
                  factFind,
                  financials: financialsBoth,
                  radInput: secondRoom!.roomPrice,
                  extraServiceFeeInput: 0,
                  radPaid: 0,
                  overallStrategy: { rentHome: false, downsizeHome: false, accessHomeEquity: false, reverseMortgage: false, heas: false, ...residencyBoth },
                  resident: secondRoom!.person,
              })) as Record<string, unknown>)
            : null;
        // The first room once both are in care — the second stage's first-room schedule.
        const firstOnceBoth = later
            ? ((await calculationApi.calculateAgedCareCosts({
                  factFind,
                  financials: financialsBoth,
                  radInput,
                  extraServiceFeeInput: 0,
                  radPaid: 0,
                  overallStrategy: { rentHome: false, downsizeHome: false, accessHomeEquity: false, reverseMortgage: false, heas: false, ...residencyBoth },
                  resident: secondRoom!.person === 'client' ? 'partner' : 'client',
              })) as Record<string, unknown>)
            : null;
        if (villageExit && villageExit.entitlement > 0) {
            doNothingOptions.villageExit = { ...villageExit, radAllocation: 0, interimDailyPayment: 0 };
        }
        if (secondAgedCareCosts) {
            doNothingOptions.secondResident = { person: secondRoom!.person, ...(secondRoom!.careStartDate ? { careStartDate: secondRoom!.careStartDate } : {}), radFunding: {}, agedCareCosts: secondAgedCareCosts, ...(livingOnceBoth != null ? { livingExpensesOnceBoth: livingOnceBoth } : {}) };
        }

        const [projections, runwayProjections] = await Promise.all([
            // PROJECTION basis — matches the estate's "after".
            calculationApi.runProjections(
                factFind,
                doNothingOptions,
                { ...agedCareCosts, scenarioLivingExpenses } as never,
                legacyYears,
            ) as Promise<Record<string, unknown>[]>,
            // RUNWAY basis — the same run over the long horizon, when a caller wants it.
            runwayYears && runwayYears > legacyYears
                ? (calculationApi.runProjections(
                    factFind,
                    doNothingOptions,
                    { ...agedCareCosts, scenarioLivingExpenses } as never,
                    runwayYears,
                ) as Promise<Record<string, unknown>[]>)
                : Promise.resolve([] as Record<string, unknown>[]),
        ]);

        const rows = Array.isArray(projections) ? projections : [];
        const last = rows[legacyYears - 1] ?? rows[rows.length - 1];
        const legacyValue = typeof last?.netWorth === 'number' ? (last.netWorth as number) : null;

        // The care fees this baseline actually incurs, netted off here so that every surface quoting
        // "cashflow with no plan" quotes the same number. See the field docs.
        // The normalised year: both rooms where both enter together; the first room alone where the
        // second person enters later (their stage is `phase2`).
        const baselineAnnualCare = ((Number(agedCareCosts?.totalDailyCost) || 0) + (later ? 0 : Number(secondAgedCareCosts?.totalDailyCost) || 0)) * 365;
        const cashFlowAfterCare = (Number(financials?.cashFlow) || 0) - baselineAnnualCare;
        const phaseTwoAnnualCare = later ? ((Number(firstOnceBoth?.totalDailyCost) || 0) + (Number(secondAgedCareCosts?.totalDailyCost) || 0)) * 365 : 0;
        const phase2: AgedCareBaselinePhaseTwo | null = later && firstOnceBoth
            ? {
                  fromDate: secondRoom!.careStartDate || '',
                  financials: financialsBoth,
                  agedCareCosts: firstOnceBoth,
                  secondAgedCareCosts,
                  cashFlowAfterCare: (Number(financialsBoth?.cashFlow) || 0) - phaseTwoAnnualCare,
                  pension: Number(financialsBoth?.socialSecurity) || 0,
              }
            : null;

        return {
            financials,
            cashFlowAfterCare,
            pension: Number(financials?.socialSecurity) || 0,
            todayFinancials,
            projections: rows,
            runwayProjections: Array.isArray(runwayProjections) ? runwayProjections : [],
            legacyValue,
            agedCareCosts,
            secondAgedCareCosts,
            secondEntersLater: later,
            phase2,
        };
    } catch (err) {
        /*
         * NEVER SWALLOW THIS SILENTLY (owner-caught, 2026-08-27).
         *
         * The bare `catch {}` this replaces was written to honour the rule above — no baseline, no
         * claim. The rule is right; the wiring defeated it. When this returns EMPTY the estate tile
         * does not say nothing: FinancialImpactPanel falls back to `before.netWorth`, captions it
         * "vs today", and reports a five-year projection against TODAY'S net worth. That is the
         * exact comparison the baseline work existed to remove — it credits a strategy with growth
         * that would have happened anyway — and it looks entirely plausible on screen.
         *
         * The owner hit it from the other end: the Suggestion Engine's best legacy read WORSE than
         * the panel's "do nothing", because the panel's figure was never a do-nothing at all. With
         * no log line there was nothing to find, which is how a failed computation passed for an
         * answer. Callers still get EMPTY; the failure is now visible to whoever has to explain it.
         */
        console.error('[agedCareBaseline] baseline failed — estate comparisons will fall back to "vs today"', err);
        return EMPTY_AGED_CARE_BASELINE;
    }
}
